import {
  configurationAuditPageSchema, configurationBindingSchema, configurationBindingUpdateSchema,
  configurationBoundAppsPageSchema, configurationCreateSchema, configurationListQuerySchema,
  configurationPageSchema, configurationPaginationSchema, configurationProfileSchema,
  configurationPublishSchema, configurationRevisionsPageSchema, configurationRollbackSchema,
  configurationStatusSchema, configurationUpdateSchema,
  type ConfigurationAuditPage, type ConfigurationBinding, type ConfigurationBindingUpdateInput,
  type ConfigurationBoundAppsPage, type ConfigurationCreateInput, type ConfigurationListQuery,
  type ConfigurationPage, type ConfigurationPagination, type ConfigurationProfile,
  type ConfigurationPublishInput, type ConfigurationRevisionsPage, type ConfigurationRollbackInput,
  type ConfigurationStatusInput, type ConfigurationUpdateInput,
} from "@yahshi/contracts";
import { createAdminRequest, type AdminHttpOptions } from "../../../lib/adminHttp";

export interface ConfigurationApi {
  list(query?: Partial<ConfigurationListQuery>): Promise<ConfigurationPage>;
  create(input: ConfigurationCreateInput): Promise<ConfigurationProfile>;
  get(id: string): Promise<ConfigurationProfile>;
  update(id: string, input: ConfigurationUpdateInput): Promise<ConfigurationProfile>;
  publish(id: string, input: ConfigurationPublishInput): Promise<ConfigurationProfile>;
  rollback(id: string, input: ConfigurationRollbackInput): Promise<ConfigurationProfile>;
  setStatus(id: string, input: ConfigurationStatusInput): Promise<ConfigurationProfile>;
  revisions(id: string, query?: Partial<ConfigurationPagination>): Promise<ConfigurationRevisionsPage>;
  bindings(id: string, query?: Partial<ConfigurationPagination>): Promise<ConfigurationBoundAppsPage>;
  audit(id: string, query?: Partial<ConfigurationPagination>): Promise<ConfigurationAuditPage>;
  getBinding(appId: string): Promise<ConfigurationBinding>;
  bind(appId: string, input: ConfigurationBindingUpdateInput): Promise<ConfigurationBinding>;
}

const base = "/admin/settings/configurations";
const profilePath = (id: string) => `${base}/${encodeURIComponent(id)}`;
const bindingPath = (appId: string) => `${base}/applications/${encodeURIComponent(appId)}/binding`;
const queryString = (value: object) => new URLSearchParams(Object.entries(value).map(([key, item]) => [key, String(item)])).toString();
const post = (value: unknown) => ({ method: "POST", body: JSON.stringify(value) });

export function createHttpConfigurationApi(options: AdminHttpOptions): ConfigurationApi {
  const request = createAdminRequest(options);
  return {
    list: (query = {}) => request(`${base}?${queryString(configurationListQuerySchema.parse(query))}`, configurationPageSchema),
    create: (input) => request(base, configurationProfileSchema, post(configurationCreateSchema.parse(input))),
    get: (id) => request(profilePath(id), configurationProfileSchema),
    update: (id, input) => request(profilePath(id), configurationProfileSchema, { method: "PATCH", body: JSON.stringify(configurationUpdateSchema.parse(input)) }),
    publish: (id, input) => request(`${profilePath(id)}/publish`, configurationProfileSchema, post(configurationPublishSchema.parse(input))),
    rollback: (id, input) => request(`${profilePath(id)}/rollback`, configurationProfileSchema, post(configurationRollbackSchema.parse(input))),
    setStatus: (id, input) => request(`${profilePath(id)}/status`, configurationProfileSchema, post(configurationStatusSchema.parse(input))),
    revisions: (id, query = {}) => request(`${profilePath(id)}/revisions?${queryString(configurationPaginationSchema.parse(query))}`, configurationRevisionsPageSchema),
    bindings: (id, query = {}) => request(`${profilePath(id)}/bindings?${queryString(configurationPaginationSchema.parse(query))}`, configurationBoundAppsPageSchema),
    audit: (id, query = {}) => request(`${profilePath(id)}/audit?${queryString(configurationPaginationSchema.parse(query))}`, configurationAuditPageSchema),
    getBinding: (appId) => request(bindingPath(appId), configurationBindingSchema),
    bind: (appId, input) => request(bindingPath(appId), configurationBindingSchema, { method: "PUT", body: JSON.stringify(configurationBindingUpdateSchema.parse(input)) }),
  };
}
